import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { X, FolderSimple, FolderSimplePlus } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function AddToFolderModal({ companyIds, onClose, onDone }) {
  const [folders, setFolders] = useState([]);
  const [newName, setNewName] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/folders").then((r) => setFolders(r.data));
  useEffect(() => { load(); }, []);

  const byId = useMemo(() => Object.fromEntries(folders.map((f) => [f.id, f])), [folders]);
  const depthOf = (f) => { let d = 0, c = f; while (c && c.parent_id) { d++; c = byId[c.parent_id]; } return d; };

  const addTo = async (fid) => {
    setSaving(true);
    try {
      const { data } = await api.post(`/folders/${fid}/companies`, { company_ids: companyIds });
      toast.success(`${data.added} cabinet(s) ajouté(s) au dossier`);
      onDone && onDone();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); setSaving(false); }
  };

  const createAndAdd = async () => {
    if (!newName.trim()) return;
    setSaving(true);
    try {
      const { data } = await api.post("/folders", { name: newName.trim() });
      await addTo(data.id);
    } catch (e) { toast.error("Erreur création du dossier"); setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="add-to-folder-modal">
      <div className="bg-white w-full max-w-md border border-border shadow-xl">
        <div className="flex items-center justify-between px-5 h-14 border-b border-border">
          <span className="font-head font-bold">Ajouter à un dossier <span className="text-muted-foreground font-normal">· {companyIds.length} cabinet(s)</span></span>
          <button onClick={onClose} data-testid="close-add-folder" className="text-muted-foreground hover:text-foreground"><X size={18} /></button>
        </div>
        <div className="max-h-72 overflow-y-auto p-2">
          {folders.length === 0 && <div className="px-3 py-6 text-center text-sm text-muted-foreground">Aucun dossier. Créez-en un ci-dessous.</div>}
          {folders.map((f) => (
            <button key={f.id} onClick={() => addTo(f.id)} disabled={saving} data-testid={`pick-folder-${f.id}`}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-secondary disabled:opacity-50"
              style={{ paddingLeft: 12 + depthOf(f) * 16 }}>
              <FolderSimple size={16} style={{ color: f.color || "#002FA7" }} weight="fill" />
              <span className="truncate">{f.name}</span>
              <span className="ml-auto text-xs text-muted-foreground">{f.count}</span>
            </button>
          ))}
        </div>
        <div className="border-t border-border p-3 flex gap-2">
          <input value={newName} onChange={(e) => setNewName(e.target.value)} data-testid="new-folder-name"
            onKeyDown={(e) => e.key === "Enter" && createAndAdd()}
            placeholder="Nouveau dossier…" className="flex-1 px-3 py-2 text-sm border border-border outline-none focus:border-primary" />
          <button onClick={createAndAdd} disabled={saving || !newName.trim()} data-testid="create-and-add-folder"
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-semibold bg-primary text-white hover:bg-blue-800 disabled:opacity-50">
            <FolderSimplePlus size={15} /> Créer
          </button>
        </div>
      </div>
    </div>
  );
}
